import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import LogoFull from '../../components/ui/LogoFull';
import LogoMark from '../../components/ui/LogoMark';
import LoginForm from './components/LoginForm';
import TrustSignals from './components/TrustSignals';
import LanguageSwitcher from '../../components/ui/LanguageSwitcher';

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is already logged in
    const user = localStorage.getItem('user');
    if (user) {
      const userData = JSON.parse(user);
      // Navigate based on user role
      switch (userData?.role) {
        case 'admin': case'manager': navigate('/analytics-dashboard');
          break;
        case 'employee': navigate('/employee-portal');
          break;
        case 'auditor': navigate('/compliance-reports');
          break;
        default:
          navigate('/analytics-dashboard');
      }
    }
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-slate-50">
      <div className="flex flex-col lg:flex-row min-h-screen">
        {/* Left Side - Branding & Information */}
        <div className="lg:w-1/2 bg-primary text-primary-foreground p-8 lg:p-12 flex flex-col justify-between">
          <div>
            {/* Logo */}
            <div className="mb-12">
              <LogoFull variant="white" size="lg" tagline={t('loginPage.tagline')} />
            </div>

            {/* Features */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold mb-4 text-white">
                  {t('loginPage.headline')}
                </h2>
                <p className="text-white/90 text-lg leading-relaxed">
                  {t('loginPage.headlineBody')}
                </p>
              </div>

              <div className="grid gap-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-accent/30 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <Icon name="Search" size={16} color="white" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1 text-white">{t('loginPage.feature1Title')}</h3>
                    <p className="text-white/80 text-sm">
                      {t('loginPage.feature1Body')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-accent/30 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <Icon name="Scale" size={16} color="white" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1 text-white">{t('loginPage.feature2Title')}</h3>
                    <p className="text-white/80 text-sm">
                      {t('loginPage.feature2Body')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-accent/30 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <Icon name="Shield" size={16} color="white" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1 text-white">{t('loginPage.feature3Title')}</h3>
                    <p className="text-white/80 text-sm">
                      {t('loginPage.feature3Body')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="lg:w-1/2 flex items-center justify-center p-8 lg:p-12 relative">
          {/* Language Switcher, top right, available before sign-in */}
          <div className="absolute top-6 right-6 z-10">
            <LanguageSwitcher />
          </div>
          <div className="w-full max-w-md space-y-8">
            {/* Header */}
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <LogoMark size={48} />
              </div>
              <h2 className="text-3xl font-bold text-foreground mb-2">{t('loginPage.welcomeBack')}</h2>
              <p className="text-muted-foreground">
                {t('loginPage.signInPrompt')}
              </p>
            </div>

            {/* Login Form */}
            <LoginForm />


            {/* Footer Links */}
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center space-x-6 text-sm">
                <button className="text-muted-foreground hover:text-primary-solid transition-smooth">
                  {t('loginPage.privacyPolicy')}
                </button>
                <button className="text-muted-foreground hover:text-primary-solid transition-smooth">
                  {t('loginPage.termsOfService')}
                </button>
                <button className="text-muted-foreground hover:text-primary-solid transition-smooth">
                  {t('loginPage.support')}
                </button>
              </div>
              
              <p className="text-xs text-muted-foreground">
                {t('loginPage.allRightsReserved', { year: new Date()?.getFullYear() })}
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Trust Signals Footer */}
      <div className="bg-card border-t border-border">
        <div className="max-w-6xl mx-auto px-8 py-12">
          <TrustSignals />
        </div>
      </div>
    </div>
  );
};

export default Login;